const PENDING_REFERRAL_CODE_KEY = 'pending_referral_code';
const PENDING_REFERRAL_REWARD_KEY = 'pending_referral_reward';
const CLAIMED_REFERRAL_CODE_PREFIX = 'claimed_referral_code';
const CLAIMING_REFERRAL_CODE_PREFIX = 'claiming_referral_code';
export const REFERRAL_CLAIM_FINISHED_EVENT = 'referral_claim_finished';
const REFERRAL_CLAIM_RESULT_KEY = 'referral_claim_result';
export const DEFAULT_INVITEE_REWARD = 100;

export function getInviteCodeForRiskSession(): string | undefined {
    if (typeof window === 'undefined') return undefined;
    try {
        return getReferralCodeFromSearch(window.location.search) ?? getPendingReferralCode() ?? undefined;
    } catch {
        return undefined;
    }
}

export interface ReferralClaimResult {
    status: 'success' | 'error';
    userId: string;
    inviteCode: string;
    response?: {
        code?: number;
    };
    error?: unknown;
}

function buildScopedReferralKey(prefix: string, userId: string, code: string): string {
    return `${prefix}:${userId}:${code}`;
}

export function getReferralCodeFromSearch(search: string): string | null {
    const searchParams = new URLSearchParams(search);
    return searchParams.get('inviteCode') || searchParams.get('gift') || searchParams.get('invite_code');
}

export function getInviteeRewardFromSearch(search: string): number | null {
    const searchParams = new URLSearchParams(search);
    const rawReward = searchParams.get('inviteeReward');
    if (rawReward === null) {
        return null;
    }

    const parsedReward = Number.parseInt(rawReward, 10);

    return Number.isFinite(parsedReward) && parsedReward >= 0
        ? parsedReward
        : null;
}

export function persistPendingReferralCode(code: string | null): void {
    if (!code) return;
    localStorage.setItem(PENDING_REFERRAL_CODE_KEY, code);
}

export function persistPendingInviteeReward(reward: number | null): void {
    if (!Number.isFinite(reward)) return;
    localStorage.setItem(PENDING_REFERRAL_REWARD_KEY, String(reward));
}

export function getPendingReferralCode(): string | null {
    return localStorage.getItem(PENDING_REFERRAL_CODE_KEY);
}

export function getPendingInviteeReward(): number | null {
    const raw = localStorage.getItem(PENDING_REFERRAL_REWARD_KEY);
    if (!raw) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
}

export function clearPendingReferralCode(): void {
    localStorage.removeItem(PENDING_REFERRAL_CODE_KEY);
    localStorage.removeItem(PENDING_REFERRAL_REWARD_KEY);
}

export function hasClaimedReferralCode(userId: string, code: string): boolean {
    return localStorage.getItem(buildScopedReferralKey(CLAIMED_REFERRAL_CODE_PREFIX, userId, code)) === '1';
}

export function setClaimedReferralCode(userId: string, code: string): void {
    localStorage.setItem(buildScopedReferralKey(CLAIMED_REFERRAL_CODE_PREFIX, userId, code), '1');
}

export function isReferralClaimInProgress(userId: string, code: string): boolean {
    return sessionStorage.getItem(buildScopedReferralKey(CLAIMING_REFERRAL_CODE_PREFIX, userId, code)) === '1';
}

export function setReferralClaimInProgress(userId: string, code: string): void {
    sessionStorage.setItem(buildScopedReferralKey(CLAIMING_REFERRAL_CODE_PREFIX, userId, code), '1');
}

export function clearReferralClaimInProgress(userId: string, code: string): void {
    sessionStorage.removeItem(buildScopedReferralKey(CLAIMING_REFERRAL_CODE_PREFIX, userId, code));
}

export function setReferralClaimResult(result: ReferralClaimResult): void {
    localStorage.setItem(REFERRAL_CLAIM_RESULT_KEY, JSON.stringify(result));
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(REFERRAL_CLAIM_FINISHED_EVENT, { detail: result }));
    }
}

export function getReferralClaimResult(): ReferralClaimResult | null {
    const raw = localStorage.getItem(REFERRAL_CLAIM_RESULT_KEY);
    if (!raw) return null;
    try {
        return JSON.parse(raw) as ReferralClaimResult;
    } catch {
        return null;
    }
}

export function didReferralClaimSucceed(
    result: ReferralClaimResult | null,
    userId: string,
    code: string
): boolean {
    if (!result) {
        return false;
    }

    if (result.status !== 'success') {
        return false;
    }

    if (result.userId !== userId || result.inviteCode !== code) {
        return false;
    }

    return result.response?.code === undefined || result.response.code === 0 || result.response.code === 200;
}
